/** Página pública do cartão de compatibilidade de casal.
 * Não requer autenticação — acedida via link partilhado nas redes sociais.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { HeartHandshake, Loader2, ArrowRight, Sparkles } from "lucide-react";

const BASE = "";

function corPor(g) {
  if (g >= 70) return "#4A5E2E";
  if (g >= 45) return "#C2831A";
  return "#B91C1C";
}

export default function CoachCasalSharePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/coach/casal/share/${token}`);
        if (!res.ok) throw new Error("Link inválido ou revogado.");
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    // Actualiza o title dinamicamente
    if (data) {
      document.title = `${data.compat}% de compatibilidade — ${data.nome1} & ${data.nome2}`;
    } else {
      document.title = "Compatibilidade de Casal — IHTP Coach Virtual";
    }
  }, [data]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F3] flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-[#4A5E2E]" />
    </div>
  );

  if (err) return (
    <div className="min-h-screen bg-[#FAF8F3] text-[#0F1829] flex flex-col items-center justify-center px-6 text-center"
      data-testid="casal-share-error">
      <HeartHandshake size={40} className="text-[#978D78]" />
      <h1 className="text-3xl font-bold mt-4">Este cartão já não está disponível</h1>
      <p className="text-[#978D78] mt-2 max-w-md">O link pode ter sido revogado ou nunca existiu.</p>
      <Link to="/my/coach" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4A5E2E] text-white font-semibold text-sm">
        Descobre o teu Coach Virtual <ArrowRight size={14} />
      </Link>
    </div>
  );

  const cor = corPor(data.compat);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF8F3] via-[#F3F0E7] to-[#EDE7D6] text-[#0F1829]"
      data-testid="casal-share-public">
      <header className="max-w-3xl mx-auto pt-8 px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full grid place-items-center bg-[#4A5E2E] text-white">
            <HeartHandshake size={16} />
          </div>
          <span className="text-base font-semibold tracking-tight">
            IHTP <span className="text-[#4A5E2E]">Coach Virtual</span>
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Cartão partilhado</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-xs uppercase tracking-widest text-[#4A5E2E]/80 mb-3">A compatibilidade de casal de</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight"
          data-testid="casal-share-nomes">
          {data.nome1} <span className="text-[#978D78]">&amp;</span> {data.nome2}
        </h1>

        <section className="mt-8 rounded-3xl border border-[#E5E7EB] bg-white overflow-hidden shadow-sm">
          <div className="p-8 sm:p-10 grid sm:grid-cols-[auto,1fr] gap-8 items-center">
            <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full grid place-items-center text-white shrink-0"
              style={{ background: cor }} data-testid="casal-share-compat">
              <div className="text-center">
                <p className="text-5xl sm:text-6xl font-black leading-none">{data.compat}%</p>
                <p className="text-xs uppercase tracking-widest opacity-80 mt-2">compatibilidade</p>
              </div>
            </div>
            <div>
              {data.resumo && (
                <p className="text-[#4B5563] leading-relaxed text-sm sm:text-base"
                  data-testid="casal-share-resumo">
                  {data.resumo}
                </p>
              )}
            </div>
          </div>
          {data.sintese && (
            <div className="px-8 sm:px-10 pb-8 sm:pb-10">
              <div className="rounded-xl bg-[#0F1829] text-white p-5 text-sm leading-relaxed"
                data-testid="casal-share-sintese">
                <p className="flex items-start gap-2">
                  <Sparkles size={16} className="shrink-0 mt-0.5 text-[#C2831A]" />
                  <span>{data.sintese}</span>
                </p>
              </div>
            </div>
          )}
        </section>

        <section className="mt-10 rounded-2xl border border-[#4A5E2E]/25 bg-[#4A5E2E]/5 p-6 text-center">
          <h3 className="text-xl font-semibold">Também queres descobrir a tua compatibilidade?</h3>
          <p className="text-sm text-[#4B5563] mt-2 max-w-lg mx-auto">
            O IHTP Coach Virtual analisa as vossas Rodas da Vida com PNL e devolve uma
            leitura profunda da vossa compatibilidade em cada área.
          </p>
          <Link to="/my/coach"
            className="inline-flex items-center gap-2 mt-5 px-6 py-2.5 rounded-full bg-[#4A5E2E] text-white font-semibold text-sm hover:opacity-90"
            data-testid="casal-share-cta">
            Começar avaliação de casal <ArrowRight size={14} />
          </Link>
        </section>

        {data.data && (
          <p className="text-[11px] text-[#978D78] text-center mt-6">
            Gerado a {new Date(data.data).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}
          </p>
        )}
      </main>
    </div>
  );
}
